import React, { useState } from 'react'
import { Box, styled, Typography, Skeleton } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { Divider, Tooltip } from 'packages/eid-ui'
import {
    useLandscapes,
    usePromoteCSVInboxLandscape,
    usePublishQueue,
    useRevalidate,
    CSV_INBOX_KEY,
} from 'hooks'
import { RTA_INBOX_TYPES } from 'utils'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import SubjectTypeAttribute from 'components/SubjectTypeAttribute'
import GenericButton from 'components/Button/GenericButton'
import ButtonWithDropdown from 'components/ButtonWithDropdown'
import useSubcomponents from 'useSubcomponents'

// Styled Components
const TimelineContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    padding: '16px 0',
}))

const LandscapeRow = styled(Box)(() => ({
    display: 'grid',
    gridTemplateColumns: 'auto 320px 1fr 320px',
    alignItems: 'flex-start',
    gap: '16px',
    width: '100%',
}))

const CheckIconColumn = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
}))

const CheckIconWrapper = styled(Box)(({ statusColor }) => ({
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: statusColor ? `${statusColor}15` : '#f5f5f5', // 15 = ~8% opacity in hex
    border: `2px solid ${statusColor || '#9e9e9e'}`,
    flexShrink: 0,
    zIndex: 1,
}))

const VerticalConnector = styled(Box)(({ isLast }) => ({
    width: '2px',
    height: '40px',
    backgroundColor: '#d0d0d0',
    display: isLast ? 'none' : 'block',
}))

const LandscapeInfo = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '320px',
    minWidth: 0, // Allow text truncation
    paddingTop: '4px', // Align with check icon center
    overflow: 'hidden', // Prevent overflow
}))

const LandscapeName = styled(Typography)(({ statusColor }) => ({
    fontSize: '14px',
    fontWeight: 600,
    color: statusColor || '#1a73e8',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}))

const LandscapeDate = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: '#5f6368',
}))

const StatsContainer = styled(Box)(() => ({
    display: 'flex',
    gap: '8px',
    fontSize: '11px',
    fontWeight: 500,
}))

const StatItem = styled(Typography)(({ color }) => ({
    fontSize: '11px',
    fontWeight: 600,
    color: color || '#5f6368',
}))

const ArrowContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    position: 'relative',
    marginTop: '14px', // Align with check icon center (32px/2 - line height)
}))

const Line = styled(Box)(({ color }) => ({
    display: 'flex',
    height: '2px',
    width: '100%',
    maxWidth: '350px',
    backgroundColor: color || '#01ae8f',
}))

const StatusBadge = styled(Box)(({ bgColor }) => ({
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '4px 16px',
    borderRadius: '4px',
    backgroundImage: bgColor || 'linear-gradient(97deg, #01ae8f, #30c17c)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    zIndex: 1,
}))

const StatusText = styled(Typography)(() => ({
    fontSize: '11px',
    fontWeight: 600,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
}))

const ActionsWrapper = styled(Box)(() => ({
    width: '100%',
    maxWidth: '320px',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexShrink: 0,
    paddingTop: '0px', // Actions have their own height, align naturally
    marginLeft: 'auto', // Push to the right edge of the column
}))

const ActionsContainer = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
}))

const PromotedByContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginLeft: '16px',
}))

const PromotedByLabel = styled(Typography)(() => ({
    fontSize: '10px',
    fontWeight: 600,
    color: '#8b909a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
}))

// Button style configurations
const outlinedButtonStyles = {
    backgroundColor: 'transparent',
    color: '#0057a0',
    border: 'solid 1px #0057a0',
    borderRadius: '4',
    padding: '8px 16px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    minWidth: 'auto',
    height: '40px',
    '&:hover': {
        backgroundColor: '#d4e4f2',
    },
}

const filledButtonStyles = {
    borderRadius: '4px',
    padding: '0px 20px',
    backgroundColor: '#0057a0',
    color: '#ffffff',
    fontSize: '14px',
    height: '40px',
    '&:hover': {
        backgroundColor: '#0057a0',
    },
}

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return (
        date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        }) +
        ' | ' +
        date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        })
    )
}

// Helper function to get status color
const getStatusColor = (statusName) => {
    switch (statusName) {
        case 'Active':
            return {
                bg: 'linear-gradient(97deg, #01ae8f, #30c17c)',
                line: '#01ae8f',
            }
        case 'Completed':
            return {
                bg: 'linear-gradient(97deg, #1a5a96, #2d7cc4)',
                line: '#1a5a96',
            }
        default:
            return {
                bg: 'linear-gradient(97deg, #757575, #9e9e9e)',
                line: '#757575',
            }
    }
}

// Helper to get check icon color
const getCheckStatus = (statusName) => {
    if (statusName === 'Completed') return 'Completed'
    if (statusName === 'Active') return 'Active'
    return 'Pending'
}
const canRevalidateControl = 'RTA-CSVInbox-CanRevalidateLandscape-Control'
const canPublishControl = 'RTA-CSVInbox-CanPublishLandscape-Control'
const canPromoteControl = 'RTA-CSVInbox-CanPromoteLandscape-Control'
const LandscapeTimelineItem = ({
    landscape,
    allLandscapes,
    isLast,
    onRevalidate,
    onPublish,
    onPromote,
    isInboxClosed,
    revalidatingLandscapeId,
    promotingLandscapeId,
    publishingLandscapeId,
    canRevalidate,
    canPublish,
    canPromote,
}) => {
    const { t } = useTranslation()
    const statusColors = getStatusColor(
        landscape.rtA_CSVInboxLandscapeStatusName,
    )
    const checkStatus = getCheckStatus(
        landscape.rtA_CSVInboxLandscapeStatusName,
    )

    const totalItems = landscape.totalItemCount || 0
    const invalidItems = landscape.totalItemValidationFailed || 0
    const validItems = landscape.totalItemValidationSucceded || 0
    const ignoredItems = landscape.totalItemIgnored || 0
    const completedItems = landscape.totalItemProcessSucceded || 0

    const isActive = landscape.rtA_CSVInboxLandscapeStatusName === 'Active'
    const isCompleted =
        landscape.rtA_CSVInboxLandscapeStatusName === 'Completed'
    const isOriginal = landscape.isOriginalLandscape

    // Show Promote To button only if it's the original landscape (regardless of status)
    const showPromoteButton = isOriginal && canPromote

    // Show Publish button when status is Active
    const showPublishButton = isActive && canPublish

    // Show Re-validate button when not completed
    const showRevalidateButton = !isCompleted && canRevalidate

    // Show Promoted By only when NOT original AND status is Completed
    const showPromotedBy = (!isOriginal && isCompleted) || isInboxClosed

    // Check if this specific landscape is being processed
    const isRevalidating =
        revalidatingLandscapeId === landscape.rtA_CSVInboxLandscapeID
    const isPublishing =
        publishingLandscapeId === landscape.rtA_CSVInboxLandscapeID
    const isPromoting =
        promotingLandscapeId === landscape.rtA_CSVInboxLandscapeID

    // Create dropdown options from all landscapes EXCEPT the current one
    const promoteToOptions =
        allLandscapes
            ?.filter(
                (l) =>
                    l.rtA_CSVInboxLandscapeID !==
                    landscape.rtA_CSVInboxLandscapeID,
            )
            ?.map((l) => ({
                label: l.landscapeFriendlyName,
                value: l.rtA_CSVInboxLandscapeID,
                onClick: () => onPromote?.(landscape, l.rtA_LandscapeID),
            })) || []

    return (
        <LandscapeRow>
            {/* Check Icon with Vertical Connector */}
            <CheckIconColumn>
                <CheckIconWrapper
                    status={checkStatus}
                    statusColor={statusColors.line}
                >
                    <Icon
                        name={checkStatus === 'Completed' ? 'Stop' : 'Approve'}
                        color={statusColors.line}
                        style={{ width: '18px', height: '18px' }}
                    />
                </CheckIconWrapper>
                <VerticalConnector isLast={isLast} />
            </CheckIconColumn>

            {/* Landscape Info */}
            <LandscapeInfo>
                <Box
                    sx={{
                        display: 'flex',
                        gap: '4px',
                        alignItems: 'center',
                        minWidth: 0, // Allow flex items to shrink
                    }}
                >
                    <LandscapeName statusColor={statusColors.line}>
                        {landscape.landscapeFriendlyName} {t('Landscape')}
                    </LandscapeName>

                    {/* <Divider vertical={true} height="1px" width="10px" /> */}
                    <LandscapeDate sx={{ flexShrink: 0 }}>
                        |
                        <Icon
                            name="Clock"
                            style={{ width: '12px', height: '12px' }}
                        />
                        {formatDate(landscape.createdDate)}
                    </LandscapeDate>
                </Box>
                <StatsContainer>
                    <StatItem color="#5f6368">
                        {t('All')} ({totalItems})
                    </StatItem>
                    {invalidItems > 0 && (
                        <StatItem color="#d32f2f">
                            {t('Invalid')} ({invalidItems})
                        </StatItem>
                    )}
                    {validItems > 0 && (
                        <StatItem color="#4caf50">
                            {t('Valid')} ({validItems})
                        </StatItem>
                    )}
                    {ignoredItems > 0 && (
                        <StatItem color="#757575">
                            {t('Ignored')} ({ignoredItems})
                        </StatItem>
                    )}
                    {completedItems > 0 && (
                        <StatItem color="#3077c1">
                            {t('Completed')} ({completedItems})
                        </StatItem>
                    )}
                </StatsContainer>
            </LandscapeInfo>

            {/* Line with Status */}
            <ArrowContainer>
                <Line color={statusColors.line} />
                <StatusBadge bgColor={statusColors.bg}>
                    <StatusText>
                        {landscape.rtA_CSVInboxLandscapeStatusName?.toUpperCase()}
                    </StatusText>
                </StatusBadge>
            </ArrowContainer>

            {/* Actions or Promoted By */}
            <ActionsWrapper>
                {showPromotedBy ? (
                    <PromotedByContainer>
                        <PromotedByLabel>{t('PromotedBy')}</PromotedByLabel>
                        <SubjectTypeAttribute
                            subjectType="Person"
                            item={{
                                friendlyName: landscape.promotedByFriendlyName,
                                imageUrl: landscape.promotedByImageUrl,
                            }}
                            personComponentProps={{
                                nameProp: 'friendlyName',
                                imgSrcProp: 'imageUrl',
                            }}
                        />
                    </PromotedByContainer>
                ) : (
                    <ActionsContainer>
                        {showRevalidateButton && (
                            <GenericButton
                                onClick={() => onRevalidate?.(landscape)}
                                rootStylesProp={
                                    isRevalidating
                                        ? {
                                              ...outlinedButtonStyles,
                                              backgroundColor: '#0057a0',
                                          }
                                        : outlinedButtonStyles
                                }
                                loading={isRevalidating}
                            >
                                {t('Re-validate')}
                            </GenericButton>
                        )}
                        {showPublishButton && (
                            <GenericButton
                                color="#2d3748"
                                onClick={() => onPublish?.(landscape)}
                                rootStylesProp={filledButtonStyles}
                                loading={isPublishing}
                            >
                                {t('Publish')}
                            </GenericButton>
                        )}
                        {showPromoteButton && (
                            <ButtonWithDropdown
                                loading={isPromoting}
                                label={t('PromoteTo')}
                                options={promoteToOptions}
                                dropdownAreaStyles={{
                                    backgroundColor: '#01ae8f',
                                    color: '#ffffff',
                                }}
                                sxProps={{
                                    backgroundColor: '#01ae8f',
                                    color: '#ffffff',
                                }}
                            />
                        )}
                    </ActionsContainer>
                )}
            </ActionsWrapper>
        </LandscapeRow>
    )
}

// Helper function to get publish queue URL based on inbox type
const getPublishQueueUrl = (inboxTypeID) => {
    switch (inboxTypeID) {
        case RTA_INBOX_TYPES.GROUP_TO_BUSINESS_ROLE:
            return '/api/RTA/grouptobusinessrole/publishqueue'
        case RTA_INBOX_TYPES.GROUP_ELIGIBILITY:
            return '/api/RTA/groupeligibility/publishqueue'
        case RTA_INBOX_TYPES.APPLICATION_ROLE_ATTRIBUTE:
            return '/api/RTA/groupattributes/publishqueue'
        case RTA_INBOX_TYPES.TEMPLATE_BUSINESS_ROLE:
            return '/api/RTA/templatebusinessrole/publishqueue'
        case RTA_INBOX_TYPES.DERIVED_BUSINESS_ROLE:
            return '/api/RTA/derivedbusinessrole/publishqueue'
        default:
            return '/api/RTA/grouptobusinessrole/publishqueue' // Default fallback
    }
}

// Helper function to get revalidate URL based on inbox type
const getRevalidateUrl = (inboxTypeID) => {
    switch (inboxTypeID) {
        case RTA_INBOX_TYPES.GROUP_TO_BUSINESS_ROLE:
            return '/api/RTA/grouptobusinessrole/revalidate'
        case RTA_INBOX_TYPES.GROUP_ELIGIBILITY:
            return '/api/RTA/groupeligibility/revalidate'
        case RTA_INBOX_TYPES.APPLICATION_ROLE_ATTRIBUTE:
            return '/api/RTA/groupattributes/revalidate'
        case RTA_INBOX_TYPES.TEMPLATE_BUSINESS_ROLE:
            return '/api/RTA/templatebusinessrole/revalidate'
        case RTA_INBOX_TYPES.DERIVED_BUSINESS_ROLE:
            return '/api/RTA/derivedbusinessrole/revalidate'
        default:
            return '/api/RTA/grouptobusinessrole/revalidate' // Default fallback
    }
}

const LandscapeTimeline = ({
    trackingID,
    isInboxClosed,
    refetch,
    inboxTypeID,
    canEditInbox,
}) => {
    const { hasAccessToControl } = useSubcomponents()
    const canRevalidate = hasAccessToControl(canRevalidateControl)
    const canPublish = hasAccessToControl(canPublishControl)
    const canPromote = hasAccessToControl(canPromoteControl)
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const {
        data: landscapes,
        isLoading,
        refetch: refetchLandscapes,
    } = useLandscapes(trackingID, false)

    // Get the appropriate URLs based on inbox type
    const revalidateUrl = getRevalidateUrl(inboxTypeID)
    const publishQueueUrl = getPublishQueueUrl(inboxTypeID)

    const { mutate: revalidate } = useRevalidate(revalidateUrl)
    const { mutate: promoteCSVInboxLandscape } = usePromoteCSVInboxLandscape()
    const { mutate: publishQueue } = usePublishQueue(publishQueueUrl)

    // Track which specific landscape is being processed
    const [revalidatingLandscapeId, setRevalidatingLandscapeId] = useState(null)
    const [publishingLandscapeId, setPublishingLandscapeId] = useState(null)
    const [promotingLandscapeId, setPromotingLandscapeId] = useState(null)
    // Filter promoted landscapes (Active or Completed) for timeline display
    const promotedLandscapes =
        landscapes?.filter(
            (l) => l.rtA_CSVInboxLandscapeStatusName !== 'Not Promoted',
        ) || []

    // Helper to refetch grid and landscapes data
    const refetchGridData = () => {
        // Invalidate GroupToBusinessRoleGrid query
        queryClient.invalidateQueries([
            CSV_INBOX_KEY,
            'LIST_GROUP_TO_BUSINESS_ROLE',
        ])
        // Also invalidate GroupEligibility query
        queryClient.invalidateQueries([CSV_INBOX_KEY, 'LIST_GROUP_ELIGIBILITY'])
        queryClient.invalidateQueries([CSV_INBOX_KEY, 'LIST_APPLICATION_ROLE_ATTRIBUTE'])
        queryClient.invalidateQueries([CSV_INBOX_KEY, 'LIST_TEMPLATE_BUSINESS_ROLE'])
        // Refetch landscapes timeline data
        refetchLandscapes()
        // Refetch parent CSV inbox data if provided
        if (refetch) refetch()
    }

    const handleRevalidate = (landscape) => {
        setRevalidatingLandscapeId(landscape.rtA_CSVInboxLandscapeID)
        revalidate(
            {
                trackingID: trackingID,
                rtA_LandscapeID: landscape.rtA_LandscapeID,
            },
            {
                onSuccess: () => {
                    setRevalidatingLandscapeId(null)
                    refetchGridData()
                },
                onError: () => {
                    setRevalidatingLandscapeId(null)
                },
            },
        )
    }

    const handlePublish = (landscape) => {
        setPublishingLandscapeId(landscape.rtA_CSVInboxLandscapeID)
        publishQueue(
            {
                trackingID: trackingID,
                rtA_LandscapeID: landscape.rtA_LandscapeID,
            },
            {
                onSuccess: () => {
                    setPublishingLandscapeId(null)
                    refetchGridData()
                },
                onError: () => {
                    setPublishingLandscapeId(null)
                },
            },
        )
    }

    const handlePromote = (fromLandscape, toLandscapeId) => {
        setPromotingLandscapeId(fromLandscape.rtA_CSVInboxLandscapeID)
        promoteCSVInboxLandscape(
            {
                trackingID: trackingID,
                targetRTA_LandscapeID: toLandscapeId,
            },
            {
                onSuccess: () => {
                    setPromotingLandscapeId(null)
                    refetchGridData()
                },
                onError: () => {
                    setPromotingLandscapeId(null)
                },
            },
        )
    }

    if (isLoading) {
        return (
            <TimelineContainer>
                {[1, 2, 3].map((i) => (
                    <Skeleton
                        sx={{ marginBottom: '16px' }}
                        key={i}
                        variant="rectangular"
                        height={40}
                    />
                ))}
            </TimelineContainer>
        )
    }

    if (!promotedLandscapes.length) {
        return (
            <Box padding="24px" textAlign="center">
                <Typography color="textSecondary">
                    {t('NoLandscapesFound')}
                </Typography>
            </Box>
        )
    }

    return (
        <TimelineContainer>
            {promotedLandscapes.map((landscape, index) => (
                <LandscapeTimelineItem
                    key={landscape.rtA_CSVInboxLandscapeID}
                    landscape={landscape}
                    allLandscapes={landscapes || []}
                    isLast={index === promotedLandscapes.length - 1}
                    onRevalidate={handleRevalidate}
                    onPublish={handlePublish}
                    onPromote={handlePromote}
                    isInboxClosed={isInboxClosed}
                    revalidatingLandscapeId={revalidatingLandscapeId}
                    promotingLandscapeId={promotingLandscapeId}
                    publishingLandscapeId={publishingLandscapeId}
                    canRevalidate={canRevalidate && canEditInbox}
                    canPublish={canPublish && canEditInbox}
                    canPromote={canPromote && canEditInbox}
                />
            ))}
        </TimelineContainer>
    )
}

export default LandscapeTimeline
